import { css } from '@emotion/react'
import type { CasAssignmentDtoT } from '@mathflat/domain/@entities/(Content)/Cas'
import {
  HandwrittenNoteControllerService,
  HandwrittenNoteModeService,
  type HandwrittenNoteTypes,
  WritableHandwrittenNote,
} from '@mathflat/handwritten-note'
import { type ReactEventHandler, useCallback, useMemo, useState } from 'react'
import { isMobile as isMobileDevice } from 'react-device-detect'

import { handwrittenNoteApi } from '~/@common/api/handwrittenNoteApi'
import { useRepository } from '~/@common/hooks/useRepository'
import { errorHandlerService } from '~/@common/services'
import { colors } from '~/@common/styles'
import 자동채점입력, { UNKNOWN_VALUE } from '~/@pages/@common/(Scoring)/(자동채점)/자동채점입력'
import 자동채점폼 from '~/@pages/@common/(Scoring)/(자동채점)/자동채점폼'
import { NoteScaleResetButton } from '~/@pages/@common/NoteScaleResetButton'
import HandwrittenNoteToolbar from '~/@pages/student/@widgets/handwritten-note/HandwrittenNoteToolbar'

import { S } from './CasSolveView.style'

type Detail = CasAssignmentDtoT.AssignedCasDetail
type Problem = CasAssignmentDtoT.CasProblemDetail

const preloadImageCss = css`
  position: absolute;
  left: -999999px;
`

type Props = {
  assignedId: number
  assignment: Detail
  problem: Problem | null
  currentInput: string
  isSubmitting: boolean
  isLastProblem: boolean
  onChangeInput: (next: string) => void
  onSubmit: () => void
  onUnknown: () => void
}

export const CasSolveView = ({
  assignedId,
  assignment,
  problem,
  currentInput,
  isSubmitting,
  isLastProblem,
  onChangeInput,
  onSubmit,
  onUnknown,
}: Props) => {
  const total = assignment.maxProblemCount ?? 0
  const scored = assignment.scoredCount ?? 0
  const currentNumber = scored + 1
  const submitLabel = isLastProblem ? '제출하기' : '제출 후 다음문제'

  const isChoice =
    !!problem &&
    (problem.type === 'SINGLE_CHOICE' ||
      problem.type === 'MULTIPLE_CHOICE' ||
      (problem.optionCount ?? 0) > 0)
  const isUnknownSelected = currentInput === UNKNOWN_VALUE
  const hasAnswer = !!currentInput

  const handleSubmitClick = () => {
    if (isUnknownSelected) {
      onUnknown()
      return
    }
    onSubmit()
  }

  const [noteContentSize, setNoteContentSize] = useState<{ width: number; height: number }>()
  const [imageUrl, setImageUrl] = useState('')
  const [timestamp, setTimestamp] = useState<number>()
  const [isResetButtonDisplayed, setIsResetButtonDisplayed] = useState(false)

  const handwrittenNoteModeService = useRepository(HandwrittenNoteModeService)
  const [noteControllerService] = useState(
    () => new HandwrittenNoteControllerService(handwrittenNoteModeService),
  )

  const handleImageLoad: ReactEventHandler<HTMLImageElement> = (e) => {
    setNoteContentSize({
      width: 1200,
      height: Math.max(729 - 40, e.currentTarget.height + 300),
    })
    if (problem?.problemImageUrl) setImageUrl(problem.problemImageUrl)
  }

  const handleImageError: ReactEventHandler<HTMLImageElement> = (e) => {
    e.currentTarget.onerror = null
  }

  const resetNoteScale = () => {
    setTimestamp(Date.now())
    setIsResetButtonDisplayed(false)
  }

  const updateIsResetButtonDisplay = ({ changed }: HandwrittenNoteTypes.TransformEventProps) => {
    setIsResetButtonDisplayed(!changed)
  }

  const problemId = problem?.id
  const readNote = useCallback(async () => {
    if (!problemId) return
    try {
      return await handwrittenNoteApi.fetchCasNote({ assignedId, problemId })
    } catch (err) {
      errorHandlerService.handle(err)
    }
  }, [assignedId, problemId])

  const writeNote = useCallback(
    async (data: Parameters<typeof handwrittenNoteApi.uploadCasNote>[1]) => {
      if (!problemId) return
      try {
        await handwrittenNoteApi.uploadCasNote({ assignedId, problemId }, data)
      } catch (err) {
        errorHandlerService.handle(err)
      }
    },
    [assignedId, problemId],
  )

  const problemImage = problem ? (
    <img src={imageUrl} width={344} alt={`문제 ${currentNumber}`} />
  ) : null
  const isCanvasReady = !!problem && !!noteContentSize

  const choiceData = useMemo(
    () =>
      problem
        ? Array.from({ length: problem.optionCount }, (_, idx) => ({
            label: String(idx + 1),
            value: String(idx + 1),
          }))
        : [],
    [problem],
  )

  return (
    <div css={S.page}>
      <div css={S.progressArea}>
        <p css={S.progressText}>
          {currentNumber}번 문제 / <span className="total">총 {total}문제</span>
        </p>
      </div>

      <div css={S.problemWrap}>
        <section css={S.card}>
          <header css={S.cardHeader}>
            <div css={S.cardHeaderLeft}>
              <span css={S.problemNumber}>{currentNumber}번</span>
              {isChoice && (problem?.answerCount ?? 0) > 0 && (
                <span css={S.answerCountBadge}>정답 {problem!.answerCount}개</span>
              )}
            </div>
            <div css={S.cardHeaderRight}>
              {isCanvasReady && (
                <HandwrittenNoteToolbar
                  top={6}
                  uiMode="simple"
                  controllerService={noteControllerService}
                />
              )}
            </div>
          </header>

          <div css={S.cardBottom}>
            {isCanvasReady && isResetButtonDisplayed && (
              <NoteScaleResetButton
                onClick={resetNoteScale}
                style={{ position: 'absolute', top: '16px', right: '16px' }}
              />
            )}
            <div css={S.problemArea}>
              {problem ? (
                isCanvasReady ? (
                  <WritableHandwrittenNote
                    key={`${problem.id}-${timestamp ?? ''}`}
                    contentSize={noteContentSize}
                    controllerService={noteControllerService}
                    readFunc={readNote}
                    writeFunc={writeNote}
                    saveDelay={100}
                    preventOverflow={isMobileDevice}
                    onTransformChange={updateIsResetButtonDisplay}
                    style={{
                      paddingBottom: '20px',
                      backgroundColor: 'var(--Colors-Gray-500)',
                      overflow: 'hidden',
                    }}
                  >
                    {problemImage}
                  </WritableHandwrittenNote>
                ) : (
                  problemImage
                )
              ) : (
                <div css={S.emptyMessage}>현재 풀 문항이 없어요.</div>
              )}
              {problem?.problemImageUrl && (
                <img
                  src={problem.problemImageUrl}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  alt=""
                  css={preloadImageCss}
                />
              )}
            </div>

            {problem && (
              <자동채점폼
                buttonText={submitLabel}
                onSubmitClick={handleSubmitClick}
                opt={{ buttonProps: { disabled: !hasAnswer || isSubmitting } }}
              >
                {isChoice ? (
                  <자동채점입력.객관식
                    max={problem.answerCount || 1}
                    values={currentInput ? currentInput.split(',') : []}
                    onChange={(next) =>
                      onChangeInput(next.map((v) => String(v)).join(','))
                    }
                    data={choiceData}
                  />
                ) : (
                  <자동채점입력.주관식
                    id={problem.id}
                    value={currentInput}
                    onChange={(next) => onChangeInput(next ?? '')}
                    keypadTypes={problem.keypadTypes}
                    schoolType={assignment.schoolType}
                    answerUnits={problem.answerUnits}
                    virtualKeybaordShowType="fixed"
                  />
                )}
              </자동채점폼>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}
